/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Yoast AI Brand Insights API
 * API for AI-powered brand insights and analysis
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AssociatedBrandUpdateRequest,
  AssociatedBrandsResponse,
  BadRequestResponse,
  Brand,
  BrandAssociationRequest,
  BrandCreate,
  BrandDetailed,
  BrandSettingValues,
  BrandSettings,
  Brands,
  CreateBrandAssociationGroup400,
  DeleteAnalysis200,
  DeleteBrandAssociationGroup400,
  DeleteSingleAnalysis200,
  ErrorResponse,
  ForbiddenResponse,
  GetAnalysis200,
  GetAnalysisList200,
  GetAnalysisStatus200,
  GetBrandQueries200,
  GetCurrentUser200,
  GetQuerySuggestions200,
  GetToken200,
  GroupedAssociatedBrand,
  Login200,
  Login401,
  LoginBody,
  Logout200,
  Logout401,
  NotFoundResponse,
  PaymentRequiredResponse,
  PostAnalysis201,
  PostAnalysisBody,
  Register201,
  Register422,
  RegisterBody,
  SuccessMessage,
  UnauthorizedResponse,
  UngroupedAssociatedBrand,
  UpdateBrandQueries200,
  UpdateBrandQueries400,
  UpdateBrandQueriesBody,
  ValidationErrorResponse,
} from './insights.schemas';

import { customInstance } from '../../orval/custom-instance';

/**
 * @summary Get list of all analysis scans for a specific brand
 */
export const getAnalysisList = (brandId: string, signal?: AbortSignal) => {
  return customInstance<GetAnalysisList200>({
    url: `/brands/${brandId}/analysis`,
    method: 'GET',
    signal,
  });
};

export const getGetAnalysisListQueryKey = (brandId: string) => {
  return [`/brands/${brandId}/analysis`] as const;
};

export const getGetAnalysisListQueryOptions = <
  TData = Awaited<ReturnType<typeof getAnalysisList>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisList>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAnalysisListQueryKey(brandId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAnalysisList>>> = ({
    signal,
  }) => getAnalysisList(brandId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!brandId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getAnalysisList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAnalysisListQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAnalysisList>>
>;
export type GetAnalysisListQueryError = UnauthorizedResponse | NotFoundResponse;

export function useGetAnalysisList<
  TData = Awaited<ReturnType<typeof getAnalysisList>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisList>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalysisList>>,
          TError,
          Awaited<ReturnType<typeof getAnalysisList>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAnalysisList<
  TData = Awaited<ReturnType<typeof getAnalysisList>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisList>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalysisList>>,
          TError,
          Awaited<ReturnType<typeof getAnalysisList>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAnalysisList<
  TData = Awaited<ReturnType<typeof getAnalysisList>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisList>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get list of all analysis scans for a specific brand
 */

export function useGetAnalysisList<
  TData = Awaited<ReturnType<typeof getAnalysisList>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisList>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetAnalysisListQueryOptions(brandId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Create and create a new analysis with scans for a brand
 */
export const postAnalysis = (
  brandId: string,
  postAnalysisBody: PostAnalysisBody,
  signal?: AbortSignal,
) => {
  return customInstance<PostAnalysis201>({
    url: `/brands/${brandId}/analysis`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postAnalysisBody,
    signal,
  });
};

export const getPostAnalysisMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAnalysis>>,
    TError,
    { brandId: string; data: PostAnalysisBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAnalysis>>,
  TError,
  { brandId: string; data: PostAnalysisBody },
  TContext
> => {
  const mutationKey = ['postAnalysis'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAnalysis>>,
    { brandId: string; data: PostAnalysisBody }
  > = (props) => {
    const { brandId, data } = props ?? {};

    return postAnalysis(brandId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAnalysisMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAnalysis>>
>;
export type PostAnalysisMutationBody = PostAnalysisBody;
export type PostAnalysisMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ValidationErrorResponse;

/**
 * @summary Create and create a new analysis with scans for a brand
 */
export const usePostAnalysis = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postAnalysis>>,
      TError,
      { brandId: string; data: PostAnalysisBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postAnalysis>>,
  TError,
  { brandId: string; data: PostAnalysisBody },
  TContext
> => {
  const mutationOptions = getPostAnalysisMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete all analysis data for a specific brand (for testing purposes)
 */
export const deleteAnalysis = (brandId: string) => {
  return customInstance<DeleteAnalysis200>({
    url: `/brands/${brandId}/analysis`,
    method: 'DELETE',
  });
};

export const getDeleteAnalysisMutationOptions = <
  TError = UnauthorizedResponse | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAnalysis>>,
    TError,
    { brandId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteAnalysis>>,
  TError,
  { brandId: string },
  TContext
> => {
  const mutationKey = ['deleteAnalysis'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteAnalysis>>,
    { brandId: string }
  > = (props) => {
    const { brandId } = props ?? {};

    return deleteAnalysis(brandId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAnalysisMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteAnalysis>>
>;

export type DeleteAnalysisMutationError =
  | UnauthorizedResponse
  | NotFoundResponse;

/**
 * @summary Delete all analysis data for a specific brand (for testing purposes)
 */
export const useDeleteAnalysis = <
  TError = UnauthorizedResponse | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteAnalysis>>,
      TError,
      { brandId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteAnalysis>>,
  TError,
  { brandId: string },
  TContext
> => {
  const mutationOptions = getDeleteAnalysisMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get analysis report with full analysis data for a specific brand and analysis
 */
export const getAnalysis = (
  brandId: string,
  analysisId: string | 'latest',
  signal?: AbortSignal,
) => {
  return customInstance<GetAnalysis200>({
    url: `/brands/${brandId}/analysis/${analysisId}`,
    method: 'GET',
    signal,
  });
};

export const getGetAnalysisQueryKey = (
  brandId: string,
  analysisId: string | 'latest',
) => {
  return [`/brands/${brandId}/analysis/${analysisId}`] as const;
};

export const getGetAnalysisQueryOptions = <
  TData = Awaited<ReturnType<typeof getAnalysis>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalysis>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAnalysisQueryKey(brandId, analysisId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAnalysis>>> = ({
    signal,
  }) => getAnalysis(brandId, analysisId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(brandId && analysisId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getAnalysis>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAnalysisQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAnalysis>>
>;
export type GetAnalysisQueryError = UnauthorizedResponse | NotFoundResponse;

export function useGetAnalysis<
  TData = Awaited<ReturnType<typeof getAnalysis>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalysis>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalysis>>,
          TError,
          Awaited<ReturnType<typeof getAnalysis>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAnalysis<
  TData = Awaited<ReturnType<typeof getAnalysis>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalysis>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalysis>>,
          TError,
          Awaited<ReturnType<typeof getAnalysis>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAnalysis<
  TData = Awaited<ReturnType<typeof getAnalysis>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalysis>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get analysis report with full analysis data for a specific brand and analysis
 */

export function useGetAnalysis<
  TData = Awaited<ReturnType<typeof getAnalysis>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAnalysis>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetAnalysisQueryOptions(brandId, analysisId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Delete a specific analysis for a brand
 */
export const deleteSingleAnalysis = (
  brandId: string,
  analysisId: string | 'latest',
) => {
  return customInstance<DeleteSingleAnalysis200>({
    url: `/brands/${brandId}/analysis/${analysisId}`,
    method: 'DELETE',
  });
};

export const getDeleteSingleAnalysisMutationOptions = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSingleAnalysis>>,
    TError,
    { brandId: string; analysisId: string | 'latest' },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSingleAnalysis>>,
  TError,
  { brandId: string; analysisId: string | 'latest' },
  TContext
> => {
  const mutationKey = ['deleteSingleAnalysis'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSingleAnalysis>>,
    { brandId: string; analysisId: string | 'latest' }
  > = (props) => {
    const { brandId, analysisId } = props ?? {};

    return deleteSingleAnalysis(brandId, analysisId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteSingleAnalysisMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSingleAnalysis>>
>;

export type DeleteSingleAnalysisMutationError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete a specific analysis for a brand
 */
export const useDeleteSingleAnalysis = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteSingleAnalysis>>,
      TError,
      { brandId: string; analysisId: string | 'latest' },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteSingleAnalysis>>,
  TError,
  { brandId: string; analysisId: string | 'latest' },
  TContext
> => {
  const mutationOptions = getDeleteSingleAnalysisMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get the status of a specific analysis for a brand
 */
export const getAnalysisStatus = (
  brandId: string,
  analysisId: string | 'latest',
  signal?: AbortSignal,
) => {
  return customInstance<GetAnalysisStatus200>({
    url: `/brands/${brandId}/analysis/${analysisId}/status`,
    method: 'GET',
    signal,
  });
};

export const getGetAnalysisStatusQueryKey = (
  brandId: string,
  analysisId: string | 'latest',
) => {
  return [`/brands/${brandId}/analysis/${analysisId}/status`] as const;
};

export const getGetAnalysisStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getAnalysisStatus>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisStatus>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAnalysisStatusQueryKey(brandId, analysisId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAnalysisStatus>>
  > = ({ signal }) => getAnalysisStatus(brandId, analysisId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(brandId && analysisId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getAnalysisStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAnalysisStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAnalysisStatus>>
>;
export type GetAnalysisStatusQueryError =
  | UnauthorizedResponse
  | NotFoundResponse;

export function useGetAnalysisStatus<
  TData = Awaited<ReturnType<typeof getAnalysisStatus>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalysisStatus>>,
          TError,
          Awaited<ReturnType<typeof getAnalysisStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAnalysisStatus<
  TData = Awaited<ReturnType<typeof getAnalysisStatus>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAnalysisStatus>>,
          TError,
          Awaited<ReturnType<typeof getAnalysisStatus>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAnalysisStatus<
  TData = Awaited<ReturnType<typeof getAnalysisStatus>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisStatus>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get the status of a specific analysis for a brand
 */

export function useGetAnalysisStatus<
  TData = Awaited<ReturnType<typeof getAnalysisStatus>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  analysisId: string | 'latest',
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAnalysisStatus>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetAnalysisStatusQueryOptions(
    brandId,
    analysisId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Authenticate a user with email and password using session-based authentication
 * @summary User login
 */
export const login = (loginBody: LoginBody, signal?: AbortSignal) => {
  return customInstance<Login200>({
    url: `/users/login`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: loginBody,
    signal,
  });
};

export const getLoginMutationOptions = <
  TError = Login401,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof login>>,
    TError,
    { data: LoginBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof login>>,
  TError,
  { data: LoginBody },
  TContext
> => {
  const mutationKey = ['login'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof login>>,
    { data: LoginBody }
  > = (props) => {
    const { data } = props ?? {};

    return login(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type LoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof login>>
>;
export type LoginMutationBody = LoginBody;
export type LoginMutationError = Login401;

/**
 * @summary User login
 */
export const useLogin = <TError = Login401, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof login>>,
      TError,
      { data: LoginBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof login>>,
  TError,
  { data: LoginBody },
  TContext
> => {
  const mutationOptions = getLoginMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * This endpoint allows you to use a MyYoast access token to get a personal access token for AIBI. The MyYoast access token must be provided in the Authorization header as a Bearer token.
 * @summary Use a MyYoast Access Token to get a AIBI personal access token
 */
export const getToken = (signal?: AbortSignal) => {
  return customInstance<GetToken200>({
    url: `/users/getToken`,
    method: 'POST',
    signal,
  });
};

export const getGetTokenMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getToken>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof getToken>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['getToken'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof getToken>>,
    void
  > = () => {
    return getToken();
  };

  return { mutationFn, ...mutationOptions };
};

export type GetTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof getToken>>
>;

export type GetTokenMutationError = unknown;

/**
 * @summary Use a MyYoast Access Token to get a AIBI personal access token
 */
export const useGetToken = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof getToken>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof getToken>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getGetTokenMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Register a new user
 * @summary User registration
 */
export const register = (registerBody: RegisterBody, signal?: AbortSignal) => {
  return customInstance<Register201>({
    url: `/users/register`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: registerBody,
    signal,
  });
};

export const getRegisterMutationOptions = <
  TError = Register422,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof register>>,
    TError,
    { data: RegisterBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof register>>,
  TError,
  { data: RegisterBody },
  TContext
> => {
  const mutationKey = ['register'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof register>>,
    { data: RegisterBody }
  > = (props) => {
    const { data } = props ?? {};

    return register(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type RegisterMutationResult = NonNullable<
  Awaited<ReturnType<typeof register>>
>;
export type RegisterMutationBody = RegisterBody;
export type RegisterMutationError = Register422;

/**
 * @summary User registration
 */
export const useRegister = <TError = Register422, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof register>>,
      TError,
      { data: RegisterBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof register>>,
  TError,
  { data: RegisterBody },
  TContext
> => {
  const mutationOptions = getRegisterMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Logout the current user and invalidate session
 * @summary User logout
 */
export const logout = (signal?: AbortSignal) => {
  return customInstance<Logout200>({
    url: `/users/logout`,
    method: 'POST',
    signal,
  });
};

export const getLogoutMutationOptions = <
  TError = Logout401,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof logout>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof logout>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['logout'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof logout>>,
    void
  > = () => {
    return logout();
  };

  return { mutationFn, ...mutationOptions };
};

export type LogoutMutationResult = NonNullable<
  Awaited<ReturnType<typeof logout>>
>;

export type LogoutMutationError = Logout401;

/**
 * @summary User logout
 */
export const useLogout = <TError = Logout401, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof logout>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof logout>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getLogoutMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get associated brands for a specific brand
 */
export const getAssociatedBrands = (brandId: string, signal?: AbortSignal) => {
  return customInstance<AssociatedBrandsResponse>({
    url: `/brands/${brandId}/associated-brands`,
    method: 'GET',
    signal,
  });
};

export const getGetAssociatedBrandsQueryKey = (brandId: string) => {
  return [`/brands/${brandId}/associated-brands`] as const;
};

export const getGetAssociatedBrandsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAssociatedBrands>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAssociatedBrands>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAssociatedBrandsQueryKey(brandId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAssociatedBrands>>
  > = ({ signal }) => getAssociatedBrands(brandId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!brandId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getAssociatedBrands>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAssociatedBrandsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAssociatedBrands>>
>;
export type GetAssociatedBrandsQueryError = UnauthorizedResponse;

export function useGetAssociatedBrands<
  TData = Awaited<ReturnType<typeof getAssociatedBrands>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAssociatedBrands>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAssociatedBrands>>,
          TError,
          Awaited<ReturnType<typeof getAssociatedBrands>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssociatedBrands<
  TData = Awaited<ReturnType<typeof getAssociatedBrands>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAssociatedBrands>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAssociatedBrands>>,
          TError,
          Awaited<ReturnType<typeof getAssociatedBrands>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetAssociatedBrands<
  TData = Awaited<ReturnType<typeof getAssociatedBrands>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAssociatedBrands>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get associated brands for a specific brand
 */

export function useGetAssociatedBrands<
  TData = Awaited<ReturnType<typeof getAssociatedBrands>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAssociatedBrands>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetAssociatedBrandsQueryOptions(brandId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update an associated brand
 */
export const updateAssociatedBrand = (
  brandId: string,
  associatedBrandId: string,
  associatedBrandUpdateRequest: AssociatedBrandUpdateRequest,
) => {
  return customInstance<UngroupedAssociatedBrand>({
    url: `/brands/${brandId}/associated-brands/${associatedBrandId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: associatedBrandUpdateRequest,
  });
};

export const getUpdateAssociatedBrandMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | NotFoundResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAssociatedBrand>>,
    TError,
    {
      brandId: string;
      associatedBrandId: string;
      data: AssociatedBrandUpdateRequest;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAssociatedBrand>>,
  TError,
  {
    brandId: string;
    associatedBrandId: string;
    data: AssociatedBrandUpdateRequest;
  },
  TContext
> => {
  const mutationKey = ['updateAssociatedBrand'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAssociatedBrand>>,
    {
      brandId: string;
      associatedBrandId: string;
      data: AssociatedBrandUpdateRequest;
    }
  > = (props) => {
    const { brandId, associatedBrandId, data } = props ?? {};

    return updateAssociatedBrand(brandId, associatedBrandId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAssociatedBrandMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateAssociatedBrand>>
>;
export type UpdateAssociatedBrandMutationBody = AssociatedBrandUpdateRequest;
export type UpdateAssociatedBrandMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | NotFoundResponse
  | ValidationErrorResponse;

/**
 * @summary Update an associated brand
 */
export const useUpdateAssociatedBrand = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | NotFoundResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateAssociatedBrand>>,
      TError,
      {
        brandId: string;
        associatedBrandId: string;
        data: AssociatedBrandUpdateRequest;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateAssociatedBrand>>,
  TError,
  {
    brandId: string;
    associatedBrandId: string;
    data: AssociatedBrandUpdateRequest;
  },
  TContext
> => {
  const mutationOptions = getUpdateAssociatedBrandMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Create a new associated brand group for a specific brand
 */
export const createBrandAssociationGroup = (
  brandId: string,
  brandAssociationRequest: BrandAssociationRequest,
  signal?: AbortSignal,
) => {
  return customInstance<GroupedAssociatedBrand>({
    url: `/brands/${brandId}/associated-brand-groups`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: brandAssociationRequest,
    signal,
  });
};

export const getCreateBrandAssociationGroupMutationOptions = <
  TError =
    | CreateBrandAssociationGroup400
    | UnauthorizedResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createBrandAssociationGroup>>,
    TError,
    { brandId: string; data: BrandAssociationRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createBrandAssociationGroup>>,
  TError,
  { brandId: string; data: BrandAssociationRequest },
  TContext
> => {
  const mutationKey = ['createBrandAssociationGroup'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createBrandAssociationGroup>>,
    { brandId: string; data: BrandAssociationRequest }
  > = (props) => {
    const { brandId, data } = props ?? {};

    return createBrandAssociationGroup(brandId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateBrandAssociationGroupMutationResult = NonNullable<
  Awaited<ReturnType<typeof createBrandAssociationGroup>>
>;
export type CreateBrandAssociationGroupMutationBody = BrandAssociationRequest;
export type CreateBrandAssociationGroupMutationError =
  | CreateBrandAssociationGroup400
  | UnauthorizedResponse
  | ValidationErrorResponse;

/**
 * @summary Create a new associated brand group for a specific brand
 */
export const useCreateBrandAssociationGroup = <
  TError =
    | CreateBrandAssociationGroup400
    | UnauthorizedResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createBrandAssociationGroup>>,
      TError,
      { brandId: string; data: BrandAssociationRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createBrandAssociationGroup>>,
  TError,
  { brandId: string; data: BrandAssociationRequest },
  TContext
> => {
  const mutationOptions =
    getCreateBrandAssociationGroupMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete a group
 */
export const deleteBrandAssociationGroup = (
  brandId: string,
  brandGroupId: string,
) => {
  return customInstance<void>({
    url: `/brands/${brandId}/associated-brand-groups/${brandGroupId}`,
    method: 'DELETE',
  });
};

export const getDeleteBrandAssociationGroupMutationOptions = <
  TError =
    | DeleteBrandAssociationGroup400
    | UnauthorizedResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBrandAssociationGroup>>,
    TError,
    { brandId: string; brandGroupId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBrandAssociationGroup>>,
  TError,
  { brandId: string; brandGroupId: string },
  TContext
> => {
  const mutationKey = ['deleteBrandAssociationGroup'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBrandAssociationGroup>>,
    { brandId: string; brandGroupId: string }
  > = (props) => {
    const { brandId, brandGroupId } = props ?? {};

    return deleteBrandAssociationGroup(brandId, brandGroupId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBrandAssociationGroupMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBrandAssociationGroup>>
>;

export type DeleteBrandAssociationGroupMutationError =
  | DeleteBrandAssociationGroup400
  | UnauthorizedResponse
  | ValidationErrorResponse;

/**
 * @summary Delete a group
 */
export const useDeleteBrandAssociationGroup = <
  TError =
    | DeleteBrandAssociationGroup400
    | UnauthorizedResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteBrandAssociationGroup>>,
      TError,
      { brandId: string; brandGroupId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteBrandAssociationGroup>>,
  TError,
  { brandId: string; brandGroupId: string },
  TContext
> => {
  const mutationOptions =
    getDeleteBrandAssociationGroupMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get list of all brands
 */
export const getBrands = (signal?: AbortSignal) => {
  return customInstance<Brands>({ url: `/brands`, method: 'GET', signal });
};

export const getGetBrandsQueryKey = () => {
  return [`/brands`] as const;
};

export const getGetBrandsQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrands>>,
  TError = UnauthorizedResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getBrands>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBrandsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBrands>>> = ({
    signal,
  }) => getBrands(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrands>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrandsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrands>>
>;
export type GetBrandsQueryError = UnauthorizedResponse;

export function useGetBrands<
  TData = Awaited<ReturnType<typeof getBrands>>,
  TError = UnauthorizedResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrands>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrands>>,
          TError,
          Awaited<ReturnType<typeof getBrands>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrands<
  TData = Awaited<ReturnType<typeof getBrands>>,
  TError = UnauthorizedResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrands>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrands>>,
          TError,
          Awaited<ReturnType<typeof getBrands>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrands<
  TData = Awaited<ReturnType<typeof getBrands>>,
  TError = UnauthorizedResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrands>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get list of all brands
 */

export function useGetBrands<
  TData = Awaited<ReturnType<typeof getBrands>>,
  TError = UnauthorizedResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrands>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrandsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Create a new brand
 */
export const createBrand = (brandCreate: BrandCreate, signal?: AbortSignal) => {
  return customInstance<Brand>({
    url: `/brands`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: brandCreate,
    signal,
  });
};

export const getCreateBrandMutationOptions = <
  TError = UnauthorizedResponse | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createBrand>>,
    TError,
    { data: BrandCreate },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createBrand>>,
  TError,
  { data: BrandCreate },
  TContext
> => {
  const mutationKey = ['createBrand'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createBrand>>,
    { data: BrandCreate }
  > = (props) => {
    const { data } = props ?? {};

    return createBrand(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateBrandMutationResult = NonNullable<
  Awaited<ReturnType<typeof createBrand>>
>;
export type CreateBrandMutationBody = BrandCreate;
export type CreateBrandMutationError =
  | UnauthorizedResponse
  | ValidationErrorResponse;

/**
 * @summary Create a new brand
 */
export const useCreateBrand = <
  TError = UnauthorizedResponse | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createBrand>>,
      TError,
      { data: BrandCreate },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createBrand>>,
  TError,
  { data: BrandCreate },
  TContext
> => {
  const mutationOptions = getCreateBrandMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get detailed information about a specific brand including its current lifecycle status
 */
export const getBrand = (brandId: string, signal?: AbortSignal) => {
  return customInstance<BrandDetailed>({
    url: `/brands/${brandId}`,
    method: 'GET',
    signal,
  });
};

export const getGetBrandQueryKey = (brandId: string) => {
  return [`/brands/${brandId}`] as const;
};

export const getGetBrandQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrand>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrand>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBrandQueryKey(brandId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBrand>>> = ({
    signal,
  }) => getBrand(brandId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!brandId,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getBrand>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetBrandQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrand>>
>;
export type GetBrandQueryError = UnauthorizedResponse | NotFoundResponse;

export function useGetBrand<
  TData = Awaited<ReturnType<typeof getBrand>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrand>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrand>>,
          TError,
          Awaited<ReturnType<typeof getBrand>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrand<
  TData = Awaited<ReturnType<typeof getBrand>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrand>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrand>>,
          TError,
          Awaited<ReturnType<typeof getBrand>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrand<
  TData = Awaited<ReturnType<typeof getBrand>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrand>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get detailed information about a specific brand including its current lifecycle status
 */

export function useGetBrand<
  TData = Awaited<ReturnType<typeof getBrand>>,
  TError = UnauthorizedResponse | NotFoundResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrand>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrandQueryOptions(brandId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Delete a brand and all associated data
 */
export const deleteBrand = (brandId: string) => {
  return customInstance<SuccessMessage>({
    url: `/brands/${brandId}`,
    method: 'DELETE',
  });
};

export const getDeleteBrandMutationOptions = <
  TError = UnauthorizedResponse | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBrand>>,
    TError,
    { brandId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBrand>>,
  TError,
  { brandId: string },
  TContext
> => {
  const mutationKey = ['deleteBrand'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBrand>>,
    { brandId: string }
  > = (props) => {
    const { brandId } = props ?? {};

    return deleteBrand(brandId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBrandMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBrand>>
>;

export type DeleteBrandMutationError = UnauthorizedResponse | NotFoundResponse;

/**
 * @summary Delete a brand and all associated data
 */
export const useDeleteBrand = <
  TError = UnauthorizedResponse | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteBrand>>,
      TError,
      { brandId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteBrand>>,
  TError,
  { brandId: string },
  TContext
> => {
  const mutationOptions = getDeleteBrandMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get settings for a specific brand
 */
export const getBrandSettings = (brandId: string, signal?: AbortSignal) => {
  return customInstance<BrandSettings>({
    url: `/brands/${brandId}/settings`,
    method: 'GET',
    signal,
  });
};

export const getGetBrandSettingsQueryKey = (brandId: string) => {
  return [`/brands/${brandId}/settings`] as const;
};

export const getGetBrandSettingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrandSettings>>,
  TError = UnauthorizedResponse | NotFoundResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandSettings>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBrandSettingsQueryKey(brandId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBrandSettings>>
  > = ({ signal }) => getBrandSettings(brandId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!brandId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrandSettings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrandSettingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrandSettings>>
>;
export type GetBrandSettingsQueryError =
  | UnauthorizedResponse
  | NotFoundResponse
  | ValidationErrorResponse;

export function useGetBrandSettings<
  TData = Awaited<ReturnType<typeof getBrandSettings>>,
  TError = UnauthorizedResponse | NotFoundResponse | ValidationErrorResponse,
>(
  brandId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandSettings>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandSettings>>,
          TError,
          Awaited<ReturnType<typeof getBrandSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrandSettings<
  TData = Awaited<ReturnType<typeof getBrandSettings>>,
  TError = UnauthorizedResponse | NotFoundResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandSettings>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandSettings>>,
          TError,
          Awaited<ReturnType<typeof getBrandSettings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrandSettings<
  TData = Awaited<ReturnType<typeof getBrandSettings>>,
  TError = UnauthorizedResponse | NotFoundResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandSettings>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get settings for a specific brand
 */

export function useGetBrandSettings<
  TData = Awaited<ReturnType<typeof getBrandSettings>>,
  TError = UnauthorizedResponse | NotFoundResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandSettings>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrandSettingsQueryOptions(brandId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update settings for a specific brand
 */
export const updateBrandSettings = (
  brandId: string,
  brandSettingValues: BrandSettingValues,
) => {
  return customInstance<BrandSettings>({
    url: `/brands/${brandId}/settings`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: brandSettingValues,
  });
};

export const getUpdateBrandSettingsMutationOptions = <
  TError =
    | UnauthorizedResponse
    | PaymentRequiredResponse
    | NotFoundResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBrandSettings>>,
    TError,
    { brandId: string; data: BrandSettingValues },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBrandSettings>>,
  TError,
  { brandId: string; data: BrandSettingValues },
  TContext
> => {
  const mutationKey = ['updateBrandSettings'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBrandSettings>>,
    { brandId: string; data: BrandSettingValues }
  > = (props) => {
    const { brandId, data } = props ?? {};

    return updateBrandSettings(brandId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBrandSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBrandSettings>>
>;
export type UpdateBrandSettingsMutationBody = BrandSettingValues;
export type UpdateBrandSettingsMutationError =
  | UnauthorizedResponse
  | PaymentRequiredResponse
  | NotFoundResponse
  | ValidationErrorResponse;

/**
 * @summary Update settings for a specific brand
 */
export const useUpdateBrandSettings = <
  TError =
    | UnauthorizedResponse
    | PaymentRequiredResponse
    | NotFoundResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateBrandSettings>>,
      TError,
      { brandId: string; data: BrandSettingValues },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateBrandSettings>>,
  TError,
  { brandId: string; data: BrandSettingValues },
  TContext
> => {
  const mutationOptions = getUpdateBrandSettingsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Get saved queries for a specific brand
 */
export const getBrandQueries = (brandId: string, signal?: AbortSignal) => {
  return customInstance<GetBrandQueries200>({
    url: `/brands/${brandId}/queries`,
    method: 'GET',
    signal,
  });
};

export const getGetBrandQueriesQueryKey = (brandId: string) => {
  return [`/brands/${brandId}/queries`] as const;
};

export const getGetBrandQueriesQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrandQueries>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandQueries>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBrandQueriesQueryKey(brandId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBrandQueries>>> = ({
    signal,
  }) => getBrandQueries(brandId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!brandId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrandQueries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrandQueriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrandQueries>>
>;
export type GetBrandQueriesQueryError = UnauthorizedResponse;

export function useGetBrandQueries<
  TData = Awaited<ReturnType<typeof getBrandQueries>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandQueries>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandQueries>>,
          TError,
          Awaited<ReturnType<typeof getBrandQueries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrandQueries<
  TData = Awaited<ReturnType<typeof getBrandQueries>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandQueries>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandQueries>>,
          TError,
          Awaited<ReturnType<typeof getBrandQueries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrandQueries<
  TData = Awaited<ReturnType<typeof getBrandQueries>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandQueries>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get saved queries for a specific brand
 */

export function useGetBrandQueries<
  TData = Awaited<ReturnType<typeof getBrandQueries>>,
  TError = UnauthorizedResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandQueries>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrandQueriesQueryOptions(brandId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update the saved queries for a specific brand
 */
export const updateBrandQueries = (
  brandId: string,
  updateBrandQueriesBody: UpdateBrandQueriesBody,
) => {
  return customInstance<UpdateBrandQueries200>({
    url: `/brands/${brandId}/queries`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: updateBrandQueriesBody,
  });
};

export const getUpdateBrandQueriesMutationOptions = <
  TError =
    | UpdateBrandQueries400
    | UnauthorizedResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBrandQueries>>,
    TError,
    { brandId: string; data: UpdateBrandQueriesBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBrandQueries>>,
  TError,
  { brandId: string; data: UpdateBrandQueriesBody },
  TContext
> => {
  const mutationKey = ['updateBrandQueries'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBrandQueries>>,
    { brandId: string; data: UpdateBrandQueriesBody }
  > = (props) => {
    const { brandId, data } = props ?? {};

    return updateBrandQueries(brandId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBrandQueriesMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBrandQueries>>
>;
export type UpdateBrandQueriesMutationBody = UpdateBrandQueriesBody;
export type UpdateBrandQueriesMutationError =
  | UpdateBrandQueries400
  | UnauthorizedResponse
  | ValidationErrorResponse;

/**
 * @summary Update the saved queries for a specific brand
 */
export const useUpdateBrandQueries = <
  TError =
    | UpdateBrandQueries400
    | UnauthorizedResponse
    | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateBrandQueries>>,
      TError,
      { brandId: string; data: UpdateBrandQueriesBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateBrandQueries>>,
  TError,
  { brandId: string; data: UpdateBrandQueriesBody },
  TContext
> => {
  const mutationOptions = getUpdateBrandQueriesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Generates 25 query suggestions using OpenAI. Limited to 1 call per hour per user.
 * @summary Generate query suggestions for a specific brand
 */
export const getQuerySuggestions = (brandId: string, signal?: AbortSignal) => {
  return customInstance<GetQuerySuggestions200>({
    url: `/brands/${brandId}/query-suggestions`,
    method: 'GET',
    signal,
  });
};

export const getGetQuerySuggestionsQueryKey = (brandId: string) => {
  return [`/brands/${brandId}/query-suggestions`] as const;
};

export const getGetQuerySuggestionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getQuerySuggestions>>,
  TError = ErrorResponse | UnauthorizedResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getQuerySuggestions>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetQuerySuggestionsQueryKey(brandId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getQuerySuggestions>>
  > = ({ signal }) => getQuerySuggestions(brandId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!brandId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getQuerySuggestions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetQuerySuggestionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getQuerySuggestions>>
>;
export type GetQuerySuggestionsQueryError =
  | ErrorResponse
  | UnauthorizedResponse
  | ValidationErrorResponse;

export function useGetQuerySuggestions<
  TData = Awaited<ReturnType<typeof getQuerySuggestions>>,
  TError = ErrorResponse | UnauthorizedResponse | ValidationErrorResponse,
>(
  brandId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getQuerySuggestions>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQuerySuggestions>>,
          TError,
          Awaited<ReturnType<typeof getQuerySuggestions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetQuerySuggestions<
  TData = Awaited<ReturnType<typeof getQuerySuggestions>>,
  TError = ErrorResponse | UnauthorizedResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getQuerySuggestions>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getQuerySuggestions>>,
          TError,
          Awaited<ReturnType<typeof getQuerySuggestions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetQuerySuggestions<
  TData = Awaited<ReturnType<typeof getQuerySuggestions>>,
  TError = ErrorResponse | UnauthorizedResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getQuerySuggestions>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Generate query suggestions for a specific brand
 */

export function useGetQuerySuggestions<
  TData = Awaited<ReturnType<typeof getQuerySuggestions>>,
  TError = ErrorResponse | UnauthorizedResponse | ValidationErrorResponse,
>(
  brandId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getQuerySuggestions>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetQuerySuggestionsQueryOptions(brandId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get current user information including their brands and account status
 */
export const getCurrentUser = (signal?: AbortSignal) => {
  return customInstance<GetCurrentUser200>({
    url: `/users/current`,
    method: 'GET',
    signal,
  });
};

export const getGetCurrentUserQueryKey = () => {
  return [`/users/current`] as const;
};

export const getGetCurrentUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCurrentUserQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({
    signal,
  }) => getCurrentUser(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCurrentUserQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCurrentUser>>
>;
export type GetCurrentUserQueryError = unknown;

export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUser>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUser>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get current user information including their brands and account status
 */

export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetCurrentUserQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Delete all brands, analysis results and query history for the current user
 */
export const deleteUserData = () => {
  return customInstance<void>({ url: `/users/data`, method: 'DELETE' });
};

export const getDeleteUserDataMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUserData>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteUserData>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['deleteUserData'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteUserData>>,
    void
  > = () => {
    return deleteUserData();
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUserDataMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteUserData>>
>;

export type DeleteUserDataMutationError = unknown;

/**
 * @summary Delete all brands, analysis results and query history for the current user
 */
export const useDeleteUserData = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteUserData>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteUserData>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getDeleteUserDataMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
